/**
 * Model metadata the client needs to say what a turn cost and how full the
 * conversation is: list prices per MTok, and the context window per model.
 *
 * The model that ran the turn rides `ChatMessage.model` (v2 contract).
 * Prices and windows are Anthropic list values (cached 2026-06); an unknown
 * model renders tokens only and no meter — an estimate from a wrong number
 * is worse than none.
 *
 * Round 5 amended usage from a pair to four numbers: the input total plus
 * how much of it was cache read and cache write. Those two are priced
 * differently, and pricing them at the full input rate is what put ~$3.17 on
 * a turn that cost a fraction of it. `costOf` below is the correction, and
 * it is the only place in the app that multiplies tokens by money.
 *
 * Round 5 (owner: "Do you think we should show dollar amount next to each
 * turn?" — yes, quietly, with a session total; "I don't need to know context
 * per turn. Keep it for overall session, like conductor") added the two
 * session-wide derivations at the bottom: what the session has spent, and how
 * much of the window it is sitting in.
 */

import type { ChatMessage } from '../../api/types';

export interface ModelPrice {
  /** USD per 1M input tokens */
  inputPerMTok: number;
  /** USD per 1M output tokens */
  outputPerMTok: number;
  /** cache-read rate as a fraction of input; absent = the generic 0.1x */
  cacheReadMultiplier?: number;
}

/**
 * The tokens one turn used, as the wire reports them (`message.done.usage`,
 * and the same fields on a persisted `ChatMessage`).
 *
 * `input_tokens` is EVERYTHING the turn read. The two cache fields are
 * SUBSETS of it, not additions: a turn with 5,214 in and 4,180 read from the
 * cache read 1,034 tokens fresh. Both are optional and may be null, which is
 * how a row written before the server reported the split looks, and also how
 * a turn that used no cache at all looks. Either way the whole input prices
 * at the full rate, which is exactly what the app did before.
 */
export interface TurnTokens {
  input_tokens: number;
  output_tokens: number;
  /** subset of input_tokens served from the cache; absent = none */
  cache_read_tokens?: number | null;
  /** subset of input_tokens written into the cache; absent = none */
  cache_write_tokens?: number | null;
}

/**
 * What the cache costs, as multiples of the model's input rate.
 *
 * Writing a token into the cache costs a quarter more than reading it fresh
 * (you pay to store it); reading one back costs a tenth. This is the round-5
 * correction: pricing every cached token at the full input rate showed the
 * owner ~$3.17 for a turn whose real bill was a fraction of that, and they
 * are paying with their own key.
 */
export const CACHE_WRITE_RATE_MULTIPLIER = 1.25;
export const CACHE_READ_RATE_MULTIPLIER = 0.1;


/** Exact model ids first; family prefixes as fallback. */
const EXACT_PRICES: Record<string, ModelPrice> = {
  // The 5.1 family reads cache at $0.25/MTok, a quarter of the generic rate.
  'claude-fable-5-1': { inputPerMTok: 10, outputPerMTok: 50, cacheReadMultiplier: 0.025 },
  'claude-mythos-5-1': { inputPerMTok: 10, outputPerMTok: 50, cacheReadMultiplier: 0.025 },
  'claude-fable-5': { inputPerMTok: 10, outputPerMTok: 50 },
  'claude-mythos-5': { inputPerMTok: 10, outputPerMTok: 50 },
  'claude-opus-5': { inputPerMTok: 5, outputPerMTok: 25 },
  'claude-opus-4-8': { inputPerMTok: 5, outputPerMTok: 25 },
  'claude-opus-4-7': { inputPerMTok: 5, outputPerMTok: 25 },
  'claude-opus-4-6': { inputPerMTok: 5, outputPerMTok: 25 },
  // Sonnet 5 is $2/$10; the $3/$15 this row carried until 2026-09-01 was
  // Sonnet 4.6's rate.
  'claude-sonnet-5': { inputPerMTok: 2, outputPerMTok: 10 },
  'claude-sonnet-4-6': { inputPerMTok: 3, outputPerMTok: 15 },
  'claude-haiku-4-5': { inputPerMTok: 1, outputPerMTok: 5 },
};

const PREFIX_PRICES: Array<[prefix: string, price: ModelPrice]> = [
  ['claude-fable-', { inputPerMTok: 10, outputPerMTok: 50 }],
  ['claude-mythos-', { inputPerMTok: 10, outputPerMTok: 50 }],
  ['claude-opus-', { inputPerMTok: 5, outputPerMTok: 25 }],
  ['claude-sonnet-', { inputPerMTok: 3, outputPerMTok: 15 }],
  ['claude-haiku-', { inputPerMTok: 1, outputPerMTok: 5 }],
];

/**
 * Context window per model, in tokens — the denominator of the session
 * meter. EXACT ids only, deliberately: prices move in family-shaped steps,
 * windows do not (Haiku 4.5 holds 200K where every Opus and Sonnet in the
 * table holds 1M), so a prefix guess here would draw a confident bar from a
 * made-up number. A model with no row renders no meter at all.
 */
const CONTEXT_WINDOWS: Record<string, number> = {
  'claude-fable-5-1': 1_000_000,
  'claude-mythos-5-1': 1_000_000,
  'claude-fable-5': 1_000_000,
  'claude-mythos-5': 1_000_000,
  'claude-opus-5': 1_000_000,
  'claude-opus-4-8': 1_000_000,
  'claude-opus-4-7': 1_000_000,
  'claude-opus-4-6': 1_000_000,
  'claude-sonnet-5': 1_000_000,
  'claude-sonnet-4-6': 1_000_000,
  'claude-haiku-4-5': 200_000,
};

export function priceForModel(model: string | null | undefined): ModelPrice | null {
  if (model === null || model === undefined) return null;
  const exact = EXACT_PRICES[model];
  if (exact !== undefined) return exact;
  for (const [prefix, price] of PREFIX_PRICES) {
    if (model.startsWith(prefix)) return price;
  }
  return null;
}

/** How many tokens this model holds, or null when we do not know. */
export function contextWindowFor(model: string | null | undefined): number | null {
  if (model === null || model === undefined) return null;
  return CONTEXT_WINDOWS[model] ?? null;
}

/** A count the wire may have sent as null, absent, or nonsense: 0 unless real. */
function tokenCount(value: number | null | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0;
}

/**
 * The ONE place tokens become dollars. Every figure on screen — per turn, in
 * the export dialog, the session total on the composer — comes through here.
 *
 * Input splits three ways, because the model charges for it three ways:
 * tokens written into the cache at 1.25x the input rate, tokens read back
 * out of it at 0.1x, and whatever is left over at the full rate. The
 * leftover is CLAMPED at zero rather than trusted: rows from before the
 * split existed, and any row whose numbers do not reconcile, must not push a
 * negative into the total.
 */
function costOf(price: ModelPrice, tokens: TurnTokens): TurnCostBreakdown {
  const cacheReadTokens = tokenCount(tokens.cache_read_tokens);
  const cacheWriteTokens = tokenCount(tokens.cache_write_tokens);
  const uncachedInputTokens = Math.max(
    0,
    tokenCount(tokens.input_tokens) - cacheReadTokens - cacheWriteTokens,
  );
  const rate = price.inputPerMTok / 1_000_000;

  const uncachedInputUsd = uncachedInputTokens * rate;
  const cacheWriteUsd = cacheWriteTokens * rate * CACHE_WRITE_RATE_MULTIPLIER;
  const cacheReadUsd =
    cacheReadTokens * rate * (price.cacheReadMultiplier ?? CACHE_READ_RATE_MULTIPLIER);
  const inputUsd = uncachedInputUsd + cacheWriteUsd + cacheReadUsd;
  const outputUsd = (tokens.output_tokens * price.outputPerMTok) / 1_000_000;
  return {
    price,
    uncachedInputTokens,
    cacheWriteTokens,
    cacheReadTokens,
    uncachedInputUsd,
    cacheWriteUsd,
    cacheReadUsd,
    inputUsd,
    outputUsd,
    totalUsd: inputUsd + outputUsd,
  };
}

/** True when this turn reported any cache traffic worth breaking out. */
export function hasCacheTraffic(tokens: TurnTokens): boolean {
  return tokenCount(tokens.cache_read_tokens) + tokenCount(tokens.cache_write_tokens) > 0;
}

/** Estimated turn cost in USD, or null when the model is unknown/unpriced. */
export function estimateTurnCostUsd(
  model: string | null | undefined,
  tokens: TurnTokens,
): number | null {
  const price = priceForModel(model);
  if (price === null) return null;
  return costOf(price, tokens).totalUsd;
}

/**
 * "~$0.14" (two decimals; "<$0.01" under a cent). The tilde is not modesty:
 * these are list prices applied to the tokens the server reported, so the
 * figure is an estimate by construction.
 */
export function formatUsdApprox(usd: number): string {
  if (usd > 0 && usd < 0.005) return '<$0.01';
  return `~$${usd.toFixed(2)}`;
}

/** One turn's cost, formatted; null when the model is unknown/unpriced. */
export function formatTurnCost(
  model: string | null | undefined,
  tokens: TurnTokens,
): string | null {
  const usd = estimateTurnCostUsd(model, tokens);
  return usd === null ? null : formatUsdApprox(usd);
}

/**
 * The full price math behind one turn's footer (owner round 2026-08-25
 * item 5: the info icon opens a real breakdown, not a dead glyph).
 * Null when the model is unknown/unpriced.
 */
export interface TurnCostBreakdown {
  price: ModelPrice;
  /** input tokens that were neither read from nor written to the cache */
  uncachedInputTokens: number;
  cacheWriteTokens: number;
  cacheReadTokens: number;
  uncachedInputUsd: number;
  cacheWriteUsd: number;
  cacheReadUsd: number;
  /** every input token, priced: the three lines above added up */
  inputUsd: number;
  outputUsd: number;
  totalUsd: number;
}

export function turnCostBreakdown(
  model: string | null | undefined,
  tokens: TurnTokens,
): TurnCostBreakdown | null {
  const price = priceForModel(model);
  if (price === null) return null;
  return costOf(price, tokens);
}

/** "5,214" (exact token counts for the breakdown; the footer keeps "5.2k"). */
export function formatTokenCount(n: number): string {
  return n.toLocaleString('en-US');
}

/** "$1.23"; small amounts keep 4 decimals ("$0.0261") so the math adds up. */
export function formatUsdDetailed(v: number): string {
  if (v !== 0 && Math.abs(v) < 0.1) return `$${v.toFixed(4)}`;
  return `$${v.toFixed(2)}`;
}

// ---------------------------------------------------------------------------
// Session-wide (round 5: cost and context belong to the conversation)
// ---------------------------------------------------------------------------

/**
 * A message's tokens, when the row recorded the input/output pair; null
 * otherwise. The cache fields ride along when present and stay undefined
 * when they are not, so a pre-round-5 row prices exactly as it used to.
 */
function tokensOf(row: {
  input_tokens?: number | null;
  output_tokens?: number | null;
  cache_read_tokens?: number | null;
  cache_write_tokens?: number | null;
}): TurnTokens | null {
  const input = row.input_tokens;
  const output = row.output_tokens;
  if (typeof input !== 'number' || typeof output !== 'number') return null;
  return {
    input_tokens: input,
    output_tokens: output,
    cache_read_tokens: row.cache_read_tokens ?? null,
    cache_write_tokens: row.cache_write_tokens ?? null,
  };
}

/**
 * What this session has spent, at list price, in USD — null when no turn in
 * it can be priced (an unknown model, or rows that never recorded tokens).
 *
 * Rows the table cannot price are skipped rather than guessed, so the number
 * is a floor, which is why it renders with a "~".
 */
export function sessionSpendUsd(messages: readonly ChatMessage[]): number | null {
  let total = 0;
  let priced = false;
  for (const message of messages) {
    if (message.role !== 'assistant') continue;
    const tokens = tokensOf(message);
    if (tokens === null) continue;
    const usd = estimateTurnCostUsd(message.model, tokens);
    if (usd === null) continue;
    total += usd;
    priced = true;
  }
  return priced ? total : null;
}

/** How full a session's context is: tokens in play against the window. */
export interface SessionContext {
  /** tokens the next request would carry */
  used: number;
  /** the model's window, in tokens */
  window: number;
  /** 0..1, clamped */
  fraction: number;
  model: string;
}

/**
 * How much of the model's window this conversation is sitting in.
 *
 * The honest measure is the NEWEST turn, not a sum: an assistant turn's
 * `input_tokens` is the whole conversation as it was sent — system prompt,
 * every earlier turn, the tools — so adding turns up would count the same
 * history once per turn and reach 400% of a window nobody filled. Newest
 * input + its output is what the next request starts from.
 *
 * Null when no turn has reported usage yet, or when the model that ran it has
 * no window on file: a bar drawn against a guessed denominator is a lie with
 * a nice shape.
 *
 * The row's own body is what the next request will carry, so it is what the
 * meter measures.
 */
function lastRequestTokens(message: ChatMessage): number | null {
  const frames = message.frames;
  if (frames === null || frames === undefined) return null;
  for (let i = frames.length - 1; i >= 0; i -= 1) {
    const frame = frames[i] as { type?: unknown; data?: { usage?: { context_tokens?: unknown } } } | undefined;
    if (frame?.type !== 'message.done') continue;
    const value = frame.data?.usage?.context_tokens;
    return typeof value === 'number' && value > 0 ? value : null;
  }
  return null;
}

export function sessionContext(messages: readonly ChatMessage[]): SessionContext | null {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i];
    if (message === undefined || message.role !== 'assistant') continue;
    const tokens = tokensOf(message);
    if (tokens === null) continue;
    const window = contextWindowFor(message.model);
    if (window === null || window <= 0 || message.model === null) return null;
    // The turn's own word on how big its LAST request was, when it said so
    // (message.done usage.context_tokens, 2026-09-07). The row's input_tokens
    // is the SUM over every request of a tool-heavy turn, which read as a
    // full window after a single question.
    const last = lastRequestTokens(message);
    const used = last !== null ? last + tokens.output_tokens : tokens.input_tokens + tokens.output_tokens;
    return {
      used,
      window,
      fraction: Math.min(1, Math.max(0, used / window)),
      model: message.model,
    };
  }
  return null;
}

/**
 * What the pickers OFFER (2026-09-01, DECISIONS #81 addendum; owner: "should
 * we remove models and just keep those 3?"): the current generation only,
 * every one of them benched. The server's wider list (models.py
 * KNOWN_MODELS) is what validates a write, so a session or product already
 * on an older id keeps working and still gets a ticked row (the picker's
 * `extra`); every id EXACT_PRICES knows can be priced, offered or not.
 */
export const PICKER_MODELS: readonly string[] = [
  'claude-fable-5-1',
  'claude-fable-5',
  'claude-opus-5',
  'claude-opus-4-8',
  'claude-sonnet-5',
];
